import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_SHAREPOINT_URL } from "constants/app";

export const INITIAL_SHAREPOINT_SETTINGS = {
  sharepointEnabled: false,
  sharepointUrl: DEFAULT_SHAREPOINT_URL,
  siteUrl: "",
  tenantId: "",
  clientId: "",
  clientSecret: "",
};

export const INITIAL_STATE = {
  settings: INITIAL_SHAREPOINT_SETTINGS,
  sharepointConnectionsTested: false,
  edits: {},
  error: false,
  isTesting: false,
  search: {
    isOpen: false,
    isSearching: false,
    value: "",
    filters: {
      categories: ["sites", "folders", "files"],
      from: "",
      to: "",
      author: "",
      tags: [],
    },
    results: {
      appliedFilters: null,
      pagination: {
        limit: 50,
        hasMore: false,
        nextStartRow: 0,
      },
    },
    loadMoreError: false,
  },
  path: [{ name: "Sites" }],
  data: [],
  collections: [],
  nextSkipToken: null,
  sharepointAuthorized: false,
  authUrl: null,
  authorizationPolicy: null,
  sharepointEnabled: false,
  restricted: false,
  policyRevision: null,
  accessCheckPending: false,
  accessCheckOwner: null,
  accessCheckSeq: 0,
  accessResults: {},
  policyResetToken: 0,
  requestSeq: 0,
  authRequestSeq: 0,
  authPending: false,
};

export const sharepointSlice = createSlice({
  name: "sharepoint",
  initialState: INITIAL_STATE,
  reducers: {
    setSharepointConnectionsTested: (
      state,
      { payload: sharepointConnectionsTested }
    ) => {
      state.sharepointConnectionsTested = Boolean(sharepointConnectionsTested);
    },
    setSharepointSettings: (state, { payload }) => {
      state.settings = payload;
    },
    updateSharepointSettings: (state, { payload }) => {
      state.settings = { ...state.settings, ...payload };
    },
    setEdits: (state, { payload }) => {
      state.edits = { ...state.edits, ...payload };
    },
    resetEdits: (state) => {
      state.edits = {};
    },
    setIsTesting: (state, { payload }) => {
      state.isTesting = payload;
    },
    setSharepointData(state, { payload = [] }) {
      state.data = payload;
    },
    setNextSkipToken(state, { payload }) {
      state.nextSkipToken = payload;
    },
    setSharepointCollections(state, { payload = [] }) {
      state.collections = payload;
    },
    setSharepointError(state, { payload }) {
      state.error = payload;
    },
    setSharepointLoading(state, { payload }) {
      state.loading = payload;
    },
    resetSharepointSearch: (state) => {
      state.search = INITIAL_STATE.search;
    },
    resetSharepointState(state) {
      state.data = [];
      state.path = INITIAL_STATE.path;
      state.nextSkipToken = null;
      state.search = { ...INITIAL_STATE.search };
      state.collections = [];
      state.error = null;
      state.authUrl = null;
      state.authorizationPolicy = null;
      state.sharepointAuthorized = false;
      state.loading = true;
      state.restricted = false;
      state.policyRevision = null;
      state.accessCheckPending = false;
      state.accessCheckOwner = null;
      state.accessResults = {};
      state.authPending = false;
    },
    setSharepointPolicyInfo(state, { payload }) {
      state.restricted = Boolean(payload?.restricted);
      state.policyRevision = payload?.policyRevision ?? null;
    },
    updateSiteAccess(state, { payload = [] }) {
      const byId = new Map(payload.map((result) => [result.id, result]));
      payload.forEach((result) => {
        if (result.accessStatus === "CHECKING") {
          delete state.accessResults[result.id];
        } else {
          state.accessResults[result.id] = {
            accessible: result.accessible ?? null,
            accessStatus: result.accessStatus,
          };
        }
      });
      state.data = state.data.map((item) =>
        item.type === "site" && byId.has(item.id)
          ? {
              ...item,
              accessible: byId.get(item.id).accessible ?? null,
              accessStatus: byId.get(item.id).accessStatus,
            }
          : item
      );
    },
    beginAccessCheck(state) {
      state.accessCheckSeq += 1;
      state.accessCheckOwner = state.accessCheckSeq;
      state.accessCheckPending = true;
    },
    endAccessCheck(state, { payload }) {
      if (state.accessCheckOwner === payload) {
        state.accessCheckPending = false;
        state.accessCheckOwner = null;
      }
    },
    nextSharepointRequest(state) {
      state.requestSeq += 1;
    },
    nextSharepointAuthRequest(state) {
      state.authRequestSeq += 1;
    },
    setAuthPending(state, { payload }) {
      state.authPending = Boolean(payload);
    },
    applyPolicyReset(state) {
      state.data = [];
      state.path = INITIAL_STATE.path;
      state.nextSkipToken = null;
      state.search = { ...INITIAL_STATE.search };
      state.collections = [];
      state.error = null;
      state.accessCheckPending = false;
      state.accessCheckOwner = null;
      state.accessResults = {};
      state.policyResetToken += 1;
    },
    setSearchLoadMoreError(state, { payload }) {
      state.search.loadMoreError = Boolean(payload);
    },
    setIsSearching(state, { payload }) {
      state.search.isSearching = payload;
    },
    setSearchPagination: (state, { payload }) => {
      const { pagination } = payload || {};
      const { limit, nextStartRow, hasMore } = pagination;

      state.search.results.pagination = {
        limit:
          limit !== undefined ? limit : state.search.results.pagination.limit,
        nextStartRow:
          nextStartRow !== undefined
            ? nextStartRow
            : state.search.results.pagination.nextStartRow,
        hasMore:
          hasMore !== undefined
            ? hasMore
            : state.search.results.pagination.hasMore,
      };
    },
    setIsSearchOpen: (state, { payload }) => {
      state.search.isOpen = payload;
    },
    setSearchValue: (state, { payload }) => {
      state.search.value = payload;
    },
    setPath: (state, { payload }) => {
      state.path = payload;
    },
    setSearchFilters: (state, { payload }) => {
      const { categories, from, to, author, tags } = payload || {};

      if (categories !== undefined) {
        state.search.filters.categories = categories;
      }
      if (from !== undefined) {
        state.search.filters.from = from;
      }
      if (to !== undefined) {
        state.search.filters.to = to;
      }
      if (author !== undefined) {
        state.search.filters.author = author;
      }
      if (tags !== undefined) {
        state.search.filters.tags = tags;
      }
    },
    updateSitePinnedStatus(state, { payload }) {
      const { siteId, isPinned } = payload;

      state.data = state.data.map((site) =>
        site.id === siteId ? { ...site, isPinned } : site
      );
    },
    setIsSharepointAuthorized(state, { payload }) {
      state.sharepointAuthorized = payload;
    },
    setIsSharepointEnabled(state, { payload }) {
      state.sharepointEnabled = payload;
    },
    setSharepointAuthUrl(state, { payload }) {
      state.authUrl = payload;
    },
    setAuthorizationPolicy(state, { payload }) {
      state.authorizationPolicy = payload ?? null;
    },
  },
});

export const {
  setSharepointConnectionsTested,
  setSharepointSettings,
  updateSharepointSettings,
  setEdits,
  resetEdits,
  setIsTesting,
  setSharepointData,
  setNextSkipToken,
  setIsSharepointEnabled,
  setSharepointError,
  setSharepointLoading,
  resetSharepointSearch,
  resetSharepointState,
  setIsSearching,
  setSearchPagination,
  setIsSearchOpen,
  setSearchFilters,
  setSearchValue,
  setPath,
  updateSitePinnedStatus,
  setIsSharepointAuthorized,
  setSharepointAuthUrl,
  setAuthorizationPolicy,
  setSharepointPolicyInfo,
  updateSiteAccess,
  beginAccessCheck,
  endAccessCheck,
  nextSharepointRequest,
  nextSharepointAuthRequest,
  setAuthPending,
  applyPolicyReset,
  setSearchLoadMoreError,
} = sharepointSlice.actions;

export default sharepointSlice.reducer;
